import { followUpListQuerySchema, type CollectionFollowUpInput, type FollowUpListQuery, type Page } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import { listFollowUps, recordFollowUp, type CollectionFollowUpView } from '../follow-ups.api';

export type FollowUpsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<CollectionFollowUpView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: FollowUpListQuery) => toListParams(query).toString();

async function loadFollowUps(customerId: string, query: FollowUpListQuery): Promise<FollowUpsState> {
  try {
    return { status: 'loaded', page: await listFollowUps(customerId, query), queryKey: keyOf(query) };
  } catch (err) {
    return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
  }
}

/** A customer's collection follow-ups, a page at a time from the server. A form failure stays inline; a recorded entry toasts. */
export function useFollowUps(customerId: string) {
  const list = useListQuery(followUpListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<FollowUpsState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchFollowUps = useCallback(() => loadFollowUps(customerId, query), [customerId, query]);

  useEffect(() => {
    let cancelled = false;
    fetchFollowUps().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchFollowUps]);

  /** Returns whether it saved, so the form stays open with its error showing when it did not. */
  async function record(input: CollectionFollowUpInput): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await recordFollowUp(customerId, input);
      setState(await fetchFollowUps());
      toast.success(input.correctsFollowUpId ? 'Correction recorded' : 'Follow-up recorded');
      return true;
    } catch (err) {
      setSaveError(apiErrorMessage(err) ?? 'Could not save — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return {
    state,
    list,
    isRefreshing: state.status === 'loaded' && state.queryKey !== keyOf(query),
    saveError,
    saving,
    resetSaveError: () => setSaveError(null),
    record,
  };
}
